// src/components/appointments/CalendarGrid.jsx
import { useState, useEffect } from "react";
import { getRadiographUrl } from "../../services/radiographyService";
import { formatDateLocal } from "../../utils/dateUtils";
import { DAY_LABEL_TO_KEY, isSlotWithinWorkingHours } from "../../utils/workingHours";
import { getReadableTextColor } from "../../utils/colorUtils";

export default function CalendarGrid({
  days,
  weekDates,
  hours,
  appointments,
  currentUser,
  workingHours,
  daySchedules,
  userColorByEmail,
  categoryColors,
  onSlotClick,
  onOpenRx,
  onDragStart,
  onDragEnd,
  onDragOverSlot,
  onDropSlot,
  onDragLeaveSlot,
  dragInfo,
  dragOverSlot,
  onResizeStart,
}) {
  const dayLabels =
    Array.isArray(days) && days.length
      ? days
      : ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
  const [thumbnails, setThumbnails] = useState({}); // { mainId: url }
  const role = currentUser?.role || null;
  const isAdmin = role === "admin";
  const normalizedUserEmail = currentUser?.email?.trim().toLowerCase() || "";
  const normalizedColorMap = userColorByEmail || {};
  const normalizedCategoryColorMap = categoryColors || {};

  // Preîncarcă thumbnails pentru programările cu Rx
  const PRELOAD_THUMBNAILS = false;

  useEffect(() => {
    if (!PRELOAD_THUMBNAILS) return undefined;
    let cancelled = false;

    const relevant = (appointments || []).filter((a) => {
      if (!a?.hasRadiograph) return false;
      if (isAdmin) return true;
      if (!normalizedUserEmail) return false;
      const createdBy = (a.createdBy || "")
        .toString()
        .trim()
        .toLowerCase();
      return createdBy === normalizedUserEmail;
    });
    const uniqueIds = [];
    const seen = new Set();
    for (const a of relevant) {
      const mainId = a.mainAppointmentId || a.id;
      if (!mainId || seen.has(mainId)) continue;
      seen.add(mainId);
      uniqueIds.push(mainId);
    }

    const missing = [];
    setThumbnails((prev) => {
      const next = {};
      for (const id of uniqueIds) {
        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          next[id] = prev[id];
        } else {
          missing.push(id);
        }
      }
      for (const [id, url] of Object.entries(prev)) {
        if (seen.has(id)) continue;
        if (
          typeof url === "string" &&
          url.startsWith("blob:") &&
          typeof URL !== "undefined" &&
          typeof URL.revokeObjectURL === "function"
        ) {
          try {
            URL.revokeObjectURL(url);
          } catch {
            /* ignore */
          }
        }
      }
      return next;
    });

    if (!missing.length) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      const fetched = {};
      for (const id of missing) {
        try {
          const url = await getRadiographUrl(id);
          if (cancelled) return;
          fetched[id] = url || null;
        } catch {
          if (cancelled) return;
          fetched[id] = null;
        }
      }
      if (!cancelled && Object.keys(fetched).length) {
        setThumbnails((prev) => ({ ...prev, ...fetched }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appointments, isAdmin, normalizedUserEmail, PRELOAD_THUMBNAILS]);

  // găsește programarea pentru zi+oră+data (!!! data locală, nu UTC)
  const getSlotApp = (day, hour, dateObj) => {
    const dateStr = formatDateLocal(dateObj); // 🔧 fix: nu mai folosim toISOString()
    return (appointments || []).find(
      (a) => a.day === day && a.hour === hour && a.date === dateStr
    );
  };

  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-1 text-center text-xs min-w-[750px]"
        style={{
          gridTemplateColumns: `repeat(${dayLabels.length + 1}, minmax(110px, 1fr))`,
        }}
      >
        {/* Header zile */}
        <div className="font-bold">Ora</div>
        {dayLabels.map((label, i) => {
          const scheduleInfo = daySchedules?.[label];
          const dateObj = weekDates[i];
          return (
            <div key={i} className="font-bold px-1 sm:px-2">
              <div className="transform scale-90 sm:scale-100">
                {label}
                <br />
                <span className="text-[10px] text-gray-500">
                  {dateObj
                    ? dateObj.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })
                    : ""}
                </span>
                {scheduleInfo?.rangeLabel ? (
                  <span
                    className={`block text-[10px] ${
                      scheduleInfo.enabled ? "text-emerald-600" : "text-rose-500"
                    }`}
                  >
                    {scheduleInfo.rangeLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}

        {/* sloturi */}
        {hours.map((hour) => {
          const isFullHour = hour % 1 === 0;
          const hourLabel = `${String(Math.floor(hour)).padStart(2, "0")}:${isFullHour ? "00" : "30"}`;

          return (
            <div key={hour} className="contents">
              {/* col ora */}
              <div
                className={`flex items-center justify-center font-bold text-[11px] min-w-[45px] px-1 h-6 sm:h-8 ${
                  isFullHour ? "border-t-2 border-gray-400" : ""
                }`}
              >
                {hourLabel}
              </div>

              {/* sloturi pe zile */}
              {dayLabels.map((day, di) => {
                const dayKey = DAY_LABEL_TO_KEY[day];
                const hasWorkingIntervals =
                  (workingHours?.[dayKey]?.intervals || []).length > 0;
                const dateObj = weekDates[di];
                const dateStr = dateObj ? formatDateLocal(dateObj) : "";
                const isDragTarget =
                  dragOverSlot &&
                  dragOverSlot.day === day &&
                  dragOverSlot.date === dateStr &&
                  Math.abs(dragOverSlot.hour - hour) < 0.001;
                const slotAllowed = isSlotWithinWorkingHours(workingHours, day, hour);
                const app = getSlotApp(day, hour, weekDates[di]);
                if (app) {
                  const creatorEmail = (app.createdBy || "")
                    .toString()
                    .trim()
                    .toLowerCase();
                  const doctorEmail = (app.doctorEmail || "")
                    .toString()
                    .trim()
                    .toLowerCase();
                  const isOwner =
                    normalizedUserEmail && creatorEmail === normalizedUserEmail;
                  const isAssignedDoctor =
                    normalizedUserEmail && doctorEmail === normalizedUserEmail;
                  const canViewDetails = isAdmin || isOwner || isAssignedDoctor;
                  const canEdit =
                    isAdmin ||
                    isOwner ||
                    (role === "doctor" && isAssignedDoctor);
                  const isSecondary =
                    app.mainAppointmentId && app.mainAppointmentId !== app.id;
                  const isOwnAppointment = isOwner || isAssignedDoctor;
                  const resolvedColor =
                    isAdmin &&
                    (normalizedColorMap[doctorEmail] || normalizedColorMap[creatorEmail] || "");
                  const categoryColor = normalizedCategoryColorMap[app.category] || "";
                  const backgroundClass = isAdmin
                    ? isOwnAppointment
                      ? "bg-blue-500"
                      : "bg-green-500"
                    : isOwnAppointment
                    ? "bg-blue-500"
                    : "bg-red-500";
                  const textColor = resolvedColor ? getReadableTextColor(resolvedColor) : "";
                  const displayLabel = canViewDetails
                    ? app.patientName || "Programare"
                    : "Ocupat";
                  const hasRecall = isAdmin && app.recall && app.recall !== "none";
                  const canSeeRadiograph = canViewDetails && !isSecondary && app.hasRadiograph;

                  const badge =
                    canSeeRadiograph ? (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenRx(app.id);
                        }}
                        className="w-6 h-5 flex items-center justify-center bg-white rounded shadow hover:bg-gray-100 cursor-pointer overflow-hidden"
                        title="Vezi radiografie"
                      >
                        {thumbnails[app.mainAppointmentId || app.id] ? (
                          <img
                            src={thumbnails[app.mainAppointmentId || app.id]}
                            alt="Rx"
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          "📷"
                        )}
                      </div>
                    ) : null;

                  const slotTitle = canViewDetails
                    ? app.patientName || "Programare"
                    : "Slot ocupat";
                  const cursorClass =
                    canViewDetails && !isSecondary ? "cursor-pointer" : "cursor-not-allowed";

                  return (
                    <div
                      key={day + hour}
                      className={`relative h-6 sm:h-8 border border-gray-200 min-w-[45px] px-1 ${
                        isFullHour ? "border-t-2" : ""
                      } ${resolvedColor ? "" : backgroundClass} ${
                        resolvedColor ? "" : "text-white"
                      } rounded-sm flex items-center justify-between ${cursorClass} ${
                        isDragTarget ? "ring-2 ring-yellow-300" : ""
                      }`}
                      style={
                        resolvedColor
                          ? {
                              backgroundColor: resolvedColor,
                              borderColor: resolvedColor,
                              borderLeftColor: categoryColor || resolvedColor,
                              borderLeftWidth: categoryColor ? "25px" : undefined,
                              color: textColor || undefined,
                            }
                          : categoryColor
                          ? {
                              borderLeftColor: categoryColor,
                              borderLeftWidth: "25px",
                            }
                          : undefined
                      }
                      onClick={() =>
                        canViewDetails && !isSecondary && onSlotClick(day, hour, app.id)
                      }
                      draggable={canEdit && !isSecondary}
                      onDragStart={(e) =>
                        canEdit && !isSecondary && onDragStart?.(e, app)
                      }
                      onDragEnd={onDragEnd}
                      onDragOver={(e) =>
                        onDragOverSlot?.(e, day, dateStr, hour, app.mainAppointmentId)
                      }
                      onDrop={(e) =>
                        onDropSlot?.(e, day, dateStr, hour, app.mainAppointmentId)
                      }
                      onDragLeave={onDragLeaveSlot}
                      title={slotTitle}
                    >
                      <span className="truncate text-[10px] sm:text-xs">
                        {isSecondary ? "" : displayLabel}
                      </span>
                      <div className="flex items-center gap-1">
                        {hasRecall && !isSecondary && <span title="Recall setat">🏳️</span>}
                        {badge}
                      </div>
                      {canEdit && !isSecondary && (
                        <div
                          data-resize-handle
                          className="absolute -bottom-0.5 left-1/4 right-1/4 h-1 rounded-full bg-white/70 cursor-ns-resize"
                          draggable
                          onDragStart={(e) => onResizeStart?.(e, app)}
                          onDragEnd={onDragEnd}
                        />
                      )}
                    </div>
                  );
                }

                // slot liber
                const slotTitle = slotAllowed
                  ? `Creează programare la ${hourLabel}`
                    : hasWorkingIntervals
                    ? "Pauză (în afara intervalului activ)"
                    : "Zi liberă";
                const slotBaseClasses = `h-6 sm:h-8 border min-w-[45px] px-1 ${
                  isFullHour ? "border-t-2" : ""
                } rounded-sm`;
                const slotStateClasses = slotAllowed
                  ? "border-gray-200 hover:bg-gray-100 cursor-pointer"
                  : hasWorkingIntervals
                  ? "border-amber-200 bg-amber-50/80 text-amber-800 cursor-not-allowed"
                  : "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed opacity-70";
                return (
                  <div
                    key={day + hour}
                    className={`${slotBaseClasses} ${slotStateClasses} ${
                      isDragTarget ? "ring-2 ring-yellow-300" : ""
                    }`}
                    onClick={() => slotAllowed && currentUser && onSlotClick(day, hour)}
                    onDragOver={(e) => dragInfo && onDragOverSlot?.(e, day, dateStr, hour)}
                    onDragLeave={onDragLeaveSlot}
                    onDrop={(e) => onDropSlot?.(e, day, dateStr, hour)}
                    title={slotTitle}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
